import React, { useRef, useCallback, useState } from 'react';
import { Upload, Info, X } from 'lucide-react';
import { FORMAT_DESCRIPTIONS, FORMAT_CATEGORIES } from '../types';

interface ImageUploadAreaProps {
  onFileSelect: (files: FileList) => void;
  isDarkMode: boolean;
}

export default function ImageUploadArea({ onFileSelect, isDarkMode }: ImageUploadAreaProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [showFormatsModal, setShowFormatsModal] = useState(false);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragActive(false);
    
    if (e.dataTransfer.files) {
      onFileSelect(e.dataTransfer.files);
    }
  }, [onFileSelect]);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragActive(false);
  }, []);



  return (
    <div className="w-full">
      {/* Área de Upload Principal - Mobile First - Responsivo */}
      <div 
        className={`relative ${
          isDarkMode 
            ? `bg-[#383D52] border-[#4A4F64] ${isDragActive ? 'border-purple-400 bg-[#4A4F64]' : 'border-dashed border-2'}`
            : `bg-white border-[#E5E5E5] ${isDragActive ? 'border-purple-400 bg-purple-50' : 'border-dashed border-2'}`
        } rounded-lg sm:rounded-xl lg:rounded-2xl p-3 sm:p-4 md:p-6 lg:p-8 transition-all duration-300 cursor-pointer hover:shadow-lg`}
        onDrop={handleDrop}
        onDragOver={handleDragEnter}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onClick={() => fileInputRef.current?.click()}
      >
        <div className="text-center">
          {/* Ícone Principal - Mobile First */}
          <div className={`relative mx-auto mb-3 sm:mb-4 md:mb-6 transition-all duration-300 ${
            isDragActive ? 'scale-110' : 'hover:scale-105'
          }`}>
            <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 bg-purple-500 rounded-lg flex items-center justify-center mx-auto relative">
              <Upload className={`w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 lg:w-8 lg:h-8 text-white transition-transform duration-300 ${
                isDragActive ? 'animate-bounce' : ''
              }`} />
            </div>
          </div>

          {/* Texto Principal - Mobile First */}
          <h3 className={`text-base sm:text-lg md:text-xl lg:text-2xl font-semibold mb-2 sm:mb-3 px-2 ${
            isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
          }`}>
            {isDragActive 
              ? 'Solte seus arquivos aqui' 
              : 'Arraste e solte suas imagens'
            }
          </h3>
          
          <p className={`text-xs sm:text-sm md:text-base mb-3 sm:mb-4 md:mb-6 px-2 ${
            isDarkMode ? 'text-[#EAECF3]/70' : 'text-[#4A4A69]/70'
          }`}>
            ou clique para selecionar do seu computador
          </p>

          {/* Botão Estilizado - Mobile First */}
          <button className="bg-purple-500 hover:bg-purple-600 text-white px-3 sm:px-4 md:px-6 lg:px-8 py-2 sm:py-2.5 md:py-3 rounded-lg font-medium shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 active:scale-95 mb-3 sm:mb-4 md:mb-6">
            <div className="flex items-center justify-center space-x-1.5 sm:space-x-2">
              <Upload className="w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5" />
              <span className="text-xs sm:text-sm md:text-base">Escolher imagens</span>
            </div>
          </button>

          {/* Input Oculto */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".png,.jpeg,.jpg,.gif,.webp,.bmp,.tiff,.tif,.svg,.ico,.hdr,.psd,.dds,.avif,.heic,.heif,.cur,.rgb,.wbmp,.tga,.jbg,.jp2,.xpm,.jfif,.jpe,.exr,.pcx,.pgm,.ppm,.map,.pdb,.jif,.g3,.xbm,.pbm,.jps,.rgba,.ras,.mtv,.pic,.picon,.jbig,.sixel,.fax,.sgi,.pct,.yuv,.pcd,.g4,.vips,.pfm,.jfi,.pgx,.six,.pnm,.ipl,.pal,.rgf,.mng,.xv,.xwd,.otb,.pam,.viff,.sun,.uyvy,.hrz,.rgbo,.palm,.fts,image/*"
            onChange={(e) => e.target.files && onFileSelect(e.target.files)}
            className="hidden"
          />

          {/* Formatos Suportados - Mobile First com Scroll Horizontal */}
          <div className="mb-2 sm:mb-3 md:mb-4">
            {/* Mobile: Scroll horizontal */}
            <div className="flex sm:hidden overflow-x-auto scrollbar-hide gap-1.5 pb-2 px-1">
              {['PNG', 'JPEG', 'GIF', 'WebP', 'AVIF', 'HEIC', 'SVG', 'TIFF'].map((format, index) => (
                <span 
                  key={format}
                  className={`px-2 py-1 text-xs font-medium rounded-lg transition-all duration-300 whitespace-nowrap ${
                    ['PNG', 'JPEG', 'GIF', 'WebP'].includes(format)
                      ? `${isDarkMode ? 'bg-green-500/20 text-green-300' : 'bg-green-100 text-green-600'} border border-green-300/30`
                      : `${isDarkMode ? 'bg-blue-500/20 text-blue-300' : 'bg-blue-100 text-blue-600'} border border-blue-300/30`
                  } hover:scale-105 cursor-pointer`}
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  {format}
                </span>
              ))}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowFormatsModal(true);
                }}
                className={`px-2 py-1 text-xs font-medium rounded-lg transition-all duration-300 whitespace-nowrap ${
                  isDarkMode ? 'bg-purple-500/20 text-purple-300' : 'bg-purple-100 text-purple-600'
                } border border-purple-300/30 hover:scale-105 cursor-pointer flex items-center space-x-1`}
              >
                <span>+</span>
                <Info className="w-3 h-3" />
              </button>
            </div>

            {/* Desktop: Flex wrap */}
            <div className="hidden sm:flex flex-wrap justify-center gap-1.5 md:gap-2">
              {['PNG', 'JPEG', 'GIF', 'WebP', 'AVIF', 'HEIC', 'SVG', 'TIFF', 'BMP', 'ICO', 'PSD', 'HDR', 'EXR', 'DDS'].map((format, index) => (
                <span 
                  key={format}
                  className={`px-2 sm:px-3 py-1 text-xs font-medium rounded-lg transition-all duration-300 ${
                    ['PNG', 'JPEG', 'GIF', 'WebP'].includes(format)
                      ? `${isDarkMode ? 'bg-green-500/20 text-green-300' : 'bg-green-100 text-green-600'} border border-green-300/30`
                      : ['AVIF', 'HEIC', 'SVG', 'TIFF'].includes(format)
                        ? `${isDarkMode ? 'bg-blue-500/20 text-blue-300' : 'bg-blue-100 text-blue-600'} border border-blue-300/30`
                        : ['BMP', 'ICO', 'PSD', 'HDR'].includes(format)
                          ? `${isDarkMode ? 'bg-purple-500/20 text-purple-300' : 'bg-purple-100 text-purple-600'} border border-purple-300/30`
                          : `${isDarkMode ? 'bg-orange-500/20 text-orange-300' : 'bg-orange-100 text-orange-600'} border border-orange-300/30`
                  } hover:scale-105 cursor-pointer`}
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  {format}
                </span>
              ))}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowFormatsModal(true);
                }}
                className={`px-2 sm:px-3 py-1 text-xs font-medium rounded-lg transition-all duration-300 ${
                  isDarkMode ? 'bg-purple-500/20 text-purple-300' : 'bg-purple-100 text-purple-600'
                } border border-purple-300/30 hover:scale-105 cursor-pointer flex items-center space-x-1`}
              >
                <span className="hidden sm:inline">+70</span>
                <span className="sm:hidden">+</span>
                <Info className="w-3 h-3" />
              </button>
            </div>
          </div>
          
          <div className={`text-xs sm:text-sm ${
            isDarkMode ? 'text-[#EAECF3]/50' : 'text-[#4A4A69]/50'
          }`}>
            Suporte para mais de 84 formatos de imagem
          </div>
        </div>
      </div>

      {/* Modal de Formatos Suportados - Responsivo */}
      {showFormatsModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-4 lg:p-6">
          <div className="border rounded-xl sm:rounded-2xl max-w-xs sm:max-w-2xl lg:max-w-4xl w-full max-h-[85vh] sm:max-h-[80vh] overflow-hidden flex flex-col" style={{ backgroundColor: '#383D52', borderColor: '#4A4F64' }}>
            {/* Header - Responsivo */}
            <div className={`p-4 sm:p-6 border-b ${
              isDarkMode ? 'border-[#4A4F64]' : 'border-[#E5E5E5]'
            } flex items-center justify-between`}>
              <h3 className={`text-lg sm:text-xl font-semibold ${
                isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
              }`}>
                <span className="hidden sm:inline">Formatos Suportados (84+ formatos)</span>
                <span className="sm:hidden">Formatos (84+)</span>
              </h3>
              <button
                onClick={() => setShowFormatsModal(false)}
                className={`p-1.5 sm:p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-[#383D52]' : 'hover:bg-[#F8F9FA]'
                }`}
              >
                <X className={`w-4 h-4 sm:w-5 sm:h-5 ${
                  isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
                }`} />
              </button>
            </div>

            {/* Content - Responsivo */}
            <div className="p-4 sm:p-6 overflow-y-auto">
              {Object.entries(FORMAT_CATEGORIES).map(([category, formats]) => (
                <div key={category} className="mb-4 sm:mb-6 last:mb-0">
                  <h4 className={`font-semibold mb-2 sm:mb-3 text-sm sm:text-base capitalize ${
                    isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
                  }`}>
                    {category === 'raster' ? 'Imagens Raster' :
                     category === 'vector' ? 'Imagens Vetoriais' :
                     category === 'raw' ? 'Formatos RAW' :
                     category === 'web' ? 'Formatos Web' :
                     category === 'print' ? 'Formatos de Impressão' :
                     category === 'legacy' ? 'Formatos Legados' :
                     category === 'specialized' ? 'Formatos Especializados' : category}
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-3">
                    {formats.map((format) => (
                      <div
                        key={format}
                        className={`p-2.5 sm:p-3 rounded-lg ${
                          isDarkMode ? 'bg-[#383D52]' : 'bg-[#F8F9FA]'
                        } hover:scale-105 transition-transform duration-200`}
                      >
                        <div className={`font-medium text-xs sm:text-sm uppercase ${
                          isDarkMode ? 'text-[#EAECF3]' : 'text-[#4A4A69]'
                        }`}>
                          {format}
                        </div>
                        <div className={`text-xs mt-1 leading-tight ${
                          isDarkMode ? 'text-[#EAECF3]/70' : 'text-[#4A4A69]/70'
                        }`}>
                          {FORMAT_DESCRIPTIONS[format as keyof typeof FORMAT_DESCRIPTIONS] || 'Formato suportado'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 